import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { BillingGate } from '@/components/billing/BillingGate'

/**
 * Guards nested routes: while the session is being restored it shows a spinner,
 * once resolved it renders the child routes for an authenticated user or
 * redirects to `/login` (preserving the attempted location) otherwise.
 *
 * **The whole location, not just its path.** This carried `pathname` alone, so
 * every query string was dropped across a sign-in and somebody deep-linked to a
 * filtered list arrived at an unfiltered one. It stopped being cosmetic with
 * `/connect?request=…`, where the parameter *is* the request: losing it left the
 * consent screen with nothing to ask about and the person reading "that link is
 * missing the request it was meant to carry" after signing in perfectly well.
 */
/**
 * Where a refused visitor should be returned to, as one string.
 *
 * Its own function so the rule can be tested without standing up a router and a
 * session: what goes wrong here is silent, and the symptom appears a page later
 * as something that reads like a different fault entirely.
 */
export function attemptedLocation(location: {
  pathname: string
  search: string
  hash: string
}): string {
  return `${location.pathname}${location.search}${location.hash}`
}

export function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ink-muted" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: attemptedLocation(location) }} />
  }

  return (
    <BillingGate>
      <Outlet />
    </BillingGate>
  )
}
